import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../contexts/ThemeContext';

interface Issue {
  type: 'offline' | 'critical';
  count: number;
  action: () => void;
}

interface NeedsAttentionCardProps {
  issues: Issue[];
  isRTL: boolean;
  isArabic: boolean;
}

export default function NeedsAttentionCard({ issues, isRTL, isArabic }: NeedsAttentionCardProps) {
  const { theme } = useTheme();

  if (issues.length === 0) {
    return (
      <View style={[styles.card, { backgroundColor: theme.colors.card }]}>
        <View style={[styles.successHeader, { flexDirection: 'row' }]}>
          <View style={styles.successIcon}>
            <Ionicons name="checkmark-circle" size={32} color="#4caf50" />
          </View>
          <View style={{ flex: 1, marginLeft: isRTL ? 0 : 12, marginRight: isRTL ? 12 : 0 }}>
            <Text
              style={[
                styles.successTitle,
                { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {isArabic ? 'جميع الأنظمة تعمل بشكل طبيعي' : 'All Systems Normal'}
            </Text>
            <Text
              style={[
                styles.successSubtitle,
                { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
              ]}
            >
              {isArabic ? 'لا توجد مشاكل تتطلب انتباهك' : 'No issues require your attention'}
            </Text>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={[styles.card, styles.attentionCard, { backgroundColor: theme.colors.card }]}>
      {/* Header */}
      <View style={[styles.header, { flexDirection: 'row' }]}>
        <View style={styles.iconContainer}>
          <Ionicons name="alert-circle" size={24} color="#ff5252" />
        </View>
        <Text
          style={[styles.title, { color: theme.colors.text, textAlign: isRTL ? 'right' : 'left' }]}
        >
          {isArabic ? 'يحتاج إلى انتباه' : 'Needs Attention'}
        </Text>
      </View>

      {/* Issues List */}
      <View style={styles.issuesList}>
        {issues.map((issue, index) => (
          <TouchableOpacity
            key={index}
            style={[
              styles.issueItem,
              {
                backgroundColor:
                  issue.type === 'critical'
                    ? 'rgba(255, 82, 82, 0.08)'
                    : 'rgba(158, 158, 158, 0.08)',
                borderLeftColor: issue.type === 'critical' ? '#ff5252' : '#9e9e9e',
                borderRightColor: issue.type === 'critical' ? '#ff5252' : '#9e9e9e',
                borderLeftWidth: isRTL ? 0 : 4,
                borderRightWidth: isRTL ? 4 : 0,
                flexDirection: 'row',
              },
            ]}
            onPress={issue.action}
            activeOpacity={0.7}
          >
            <View style={{ flex: 1 }}>
              <Text
                style={[
                  styles.issueCount,
                  {
                    color: issue.type === 'critical' ? '#ff5252' : '#9e9e9e',
                    textAlign: isRTL ? 'right' : 'left',
                  },
                ]}
              >
                {issue.count}
              </Text>
              <Text
                style={[
                  styles.issueLabel,
                  { color: theme.colors.textSecondary, textAlign: isRTL ? 'right' : 'left' },
                ]}
              >
                {issue.type === 'critical'
                  ? isArabic
                    ? 'إنذار حرج'
                    : 'Critical Alarm'
                  : isArabic
                  ? 'جهاز غير متصل'
                  : 'Offline Device'}
                {issue.count > 1 ? (isArabic ? '' : 's') : ''}
              </Text>
            </View>
            <Ionicons
              name={isRTL ? 'chevron-back' : 'chevron-forward'}
              size={20}
              color={theme.colors.textSecondary}
            />
          </TouchableOpacity>
        ))}
      </View>

      {/* CTA */}
      <TouchableOpacity
        style={[styles.cta, { backgroundColor: theme.colors.primary }]}
        onPress={issues[0].action}
        activeOpacity={0.8}
      >
        <Text style={styles.ctaText}>{isArabic ? 'عرض التفاصيل' : 'View Details'}</Text>
        <Ionicons name={isRTL ? 'arrow-back' : 'arrow-forward'} size={18} color="#ffffff" />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 16,
    marginHorizontal: 16,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  attentionCard: {
    borderWidth: 1,
    borderColor: 'rgba(255, 82, 82, 0.2)',
    elevation: 4,
  },
  header: {
    alignItems: 'center',
    marginBottom: 16,
    gap: 10,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 82, 82, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    flex: 1,
    fontSize: 18,
    fontWeight: '700',
  },
  issuesList: {
    gap: 10,
    marginBottom: 16,
  },
  issueItem: {
    borderRadius: 12,
    padding: 14,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  issueCount: {
    fontSize: 26,
    fontWeight: '800',
    marginBottom: 2,
  },
  issueLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  cta: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
  },
  ctaText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#ffffff',
    letterSpacing: 0.3,
  },
  successHeader: {
    alignItems: 'center',
  },
  successIcon: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(76, 175, 80, 0.12)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 4,
  },
  successSubtitle: {
    fontSize: 14,
    fontWeight: '400',
  },
});
